import {
  Component,
  signal,
  inject,
  input,
  effect,
  output,
  computed,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Angular Material Imports
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

// Lucide Icons
import { LucideAngularModule, Info } from 'lucide-angular';
import { ItemSelectorComponent } from './item-selector/item-selector.component';
import { Subscription, firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AddListDialog } from './add-list-dialog/add-list-dialog';
import { ListOptionManager } from './list-option-manager/list-option-manager';
import { SurveyListStore } from '../base-classes/survey-list-controller.state';
import { SurveyListOptionService } from '../survey-list-option/survey-list-option';
import { ListType, SurveyListOption } from '@hyper-inferno/survey-elements-core';
import { DynamicListPreview } from "./list-option-manager/dynamic-list-preview/dynamic-list-preview";
import { AddItemDialogComponent } from './add-item-dialog/add-item-dialog.component';

@Component({
  selector: 'lib-list-manager',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatIconModule,
    LucideAngularModule,
    ItemSelectorComponent,
    MatDialogModule,
    ListOptionManager,
    DynamicListPreview
],
  templateUrl: './list-manager.html',
  styleUrl: './list-manager.scss',
})
export class ListManagerComponent {
  readonly store = inject(SurveyListStore);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  es_entityName = input.required<string>();
  elementId = input.required<string | undefined>();
  surveyId = signal<number | undefined>(undefined);
  readonly InfoIcon = Info;

  listSet = output<string | undefined>();
  private listOptionService = inject(SurveyListOptionService);
  private subscriptions = new Subscription();
  listType = ListType;
  constructor() {
    effect(() => {
      this.store.setActiveList(this.elementId());
    });

    effect(() => {
      const activeListId = this.store.activeListId();
      this.listSet.emit(activeListId);
      if ( this.store.activeList()?.list_type === ListType.CONSTRUCTED_LIST && activeListId) {
        this.updateDynamicPreviewItems(activeListId);
      } else {
        this.dynamicPreviewItems.set([]); 
      }
    });

    this.route.parent?.params.subscribe((params) => {
      const id = params['id'];
      this.surveyId.set(id ? Number(id) : undefined);
      if (id) {
        this.store.loadLists(id);
      } else {
        this.store.reset();
      }
    });
  }

  // async openListCreateModal() {
  //   if (this.store.creatingList()) {
  //     return;
  //   }
  //   const dialogRef = this.dialog.open(AddListDialog, {
  //     width: '500px',
  //     maxWidth: '95vw',
  //     disableClose: false,
  //     data: {
  //       surveyId: this.surveyId(),
  //       es_entityName: this.es_entityName(),
  //       elementId: this.elementId(),
  //     },
  //   });

  //   const result = await firstValueFrom(dialogRef.afterClosed());

  //   if (result?.success) {
  //     await this.store.createListAndUpdateEntity(result.data).then((d) => {
  //       this.changeList(d?.listId);
  //       this.listSet.emit(d?.listId);
  //     });
  //   }
  // }

  changeList(listId: string) {
    this.store.setActiveList(listId);
    if (this.store.activeList()?.list_type === ListType.CONSTRUCTED_LIST) {
      this.updateDynamicPreviewItems(listId);
    } else {
      this.dynamicPreviewItems.set([]);
    }
  }
  @ViewChild(ListOptionManager) listOptionManager!: ListOptionManager;
  ngOnInit() {
    this.subscriptions.add(
      this.listOptionService.listUpdated$.subscribe((listId) => {
        const currentListId = this.store.activeListId();
        // If the updated list is the currently active list, refresh the options
        if (currentListId === listId && this.listOptionManager) {
          this.listOptionManager.reloadItems(listId);
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
   
  dynamicPreviewItems = signal<SurveyListOption[]>([]);

  async updateDynamicPreviewItems(listId: string) {
    const items = await this.getdynamicPreviewItems(listId);
    this.dynamicPreviewItems.set(items);
  }
  
  async getdynamicPreviewItems(list_id: string): Promise<SurveyListOption[]> {
    const lists = this.store.lists();
    const currentList = lists?.find(list => list.id === list_id);
    
    if (!currentList) {
      return [];
    }
    let options: SurveyListOption[] = [];
    
    if (currentList.list_type === ListType.CONSTRUCTED_LIST && currentList.primary_list_id) {
       options = await firstValueFrom(
        this.listOptionService.getListOptions(currentList.primary_list_id)
      ); 
    } else {
       options = await firstValueFrom(
        this.listOptionService.getListOptions(list_id)
      );
    }
    return options.sort((a, b) => Number(a.option_key) - Number(b.option_key));
  }

  handleEditDynamicList() {
    const activeList = this.store.activeList();
    const activeListId = this.store.activeListId();
    if (!activeListId || !activeList) return;
    
    const dialogRef = this.dialog.open(AddItemDialogComponent, {
      width: '900px',
      data: {
        listType: ListType.CONSTRUCTED_LIST,
        surveyId: this.surveyId(),
        selectedListId: activeListId,
        es_entityName: this.es_entityName(),
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        if (this.surveyId()) {
          this.store.loadLists(this.surveyId()!);
        }
        this.updateDynamicPreviewItems(activeListId);
      }
    });
  }
}

